<template>
  <div class="dropdown-overlay" @click.self="closeDropdown" ref="refRoot" tabindex="0">
    <div ref="dropdownRef" class="dropdown-content" @click="closeDropdown">
      <slot></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue';

import { useKeyPress } from '@/composables/shared/useKeyPress';

const emit = defineEmits(['close']);

const props = defineProps({
  targetEl: {
    type: HTMLElement,
    default: null,
  },
  isVisible: {
    type: Boolean,
  },
});

const dropdownRef = ref<HTMLElement | null>(null);
const refRoot = ref<HTMLElement | null>(null);

const closeDropdown = () => {
  emit('close');
};

const updateDropdownPosition = async () => {
  await nextTick();
  if (dropdownRef.value && props.targetEl) {
    const targetRect = props.targetEl.getBoundingClientRect();
    const dropdownRect = dropdownRef.value.getBoundingClientRect();

    // 버튼의 오른쪽에 위치 (여백 4px)
    let left = targetRect.right + 4;

    // 화면 오른쪽 끝을 넘어가는 경우 버튼 왼쪽에 배치
    if (left + dropdownRect.width > window.innerWidth - 8) {
      left = targetRect.left - dropdownRect.width - 4;
    }

    // 버튼과 수직 정렬 (버튼 중앙에 맞춤)
    let top = targetRect.top + targetRect.height / 2 - dropdownRect.height / 2;

    // 화면 상단을 넘어가는 경우
    if (top < 16) {
      top = 16;
    }

    // 화면 하단을 넘어가는 경우
    if (top + dropdownRect.height > window.innerHeight - 16) {
      top = window.innerHeight - dropdownRect.height - 16;
    }

    dropdownRef.value.style.left = `${left}px`;
    dropdownRef.value.style.top = `${top}px`;

    dropdownRef.value.style.maxHeight = `${window.innerHeight - 32}px`;
    dropdownRef.value.style.overflowY = 'auto';
  }
};

useKeyPress(refRoot, {
  Escape: () => emit('close'),
});

onMounted(() => {
  refRoot.value?.focus();
  document.body.style.overflow = 'hidden';
  window.addEventListener('resize', updateDropdownPosition);
  updateDropdownPosition();
});

onUnmounted(() => {
  document.body.style.overflow = '';
  window.removeEventListener('resize', updateDropdownPosition);
});

watch(
  () => props.isVisible,
  (val) => {
    if (val) updateDropdownPosition();
  }
);
</script>

<style scoped lang="scss">
.dropdown-overlay {
  position: fixed;
  inset: 0;
  z-index: $z-dropdown;
}

.dropdown-content {
  position: absolute;
  display: flex;
  flex-direction: column;
  width: fit-content;
  gap: 0.5rem;
  padding: 0.5rem;
  border: 1px solid var(--border-default);
  border-radius: 12px;
  background: var(--surface-default);
  box-shadow: $shadow-dropdown;
  overflow-y: auto;
}
</style>
